import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import FetchParticipant from "../../api/FetchParticipant";
import "../../styles/views/participant-dashboard-view.css";
import ModifyParticipant from "../../api/ModifyParticipant";
import FetchParticipantGatherings from "../../api/FetchParticipantGatherings";
import Modal from "../../components/Modal/Modal";
import { PartialParticipantSchema } from "../../data/validationSchemas/ParticipantSchema";
import ModifyParticipantGatherings from "../../api/ModifyParticipantGatherings";
import uploadImage from "../../api/UploadImage";
import DeleteExistingImage from "../../api/DeleteExistingImage";
import FetchEvents from "../../api/FetchEvents";
import ScheduleList from "../../components/ScheduleList/ScheduleList";
import { normalizeOptionalUrl } from "../../logic/normalizeOptionalUrl";

const ParticipantDashboard = ({ type }) => {
  const { participantID } = useParams();
  const navigate = useNavigate();

  const [participantData, setParticipantData] = useState({
    name: "",
    email: "",
    website: "",
    description: "",
    gatheringIDs: [],
    participantReferenceID: "",
    imagePath: "",
    socialLinks: [],
  });
  const [newSocialLink, setNewSocialLink] = useState({ name: "", url: "" });
  const [gatherings, setGatherings] = useState([]);

  const [selectedGathering, setSelectedGathering] = useState(null);
  const [isGatheringModalOpen, setIsGatheringModalOpen] = useState(false);

  const [isAddGatheringModalOpen, setIsAddGatheringModalOpen] = useState(false);
  const [newGatheringReferenceID, setNewGatheringReferenceID] = useState("");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [events, setEvents] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [isRemovingParticipant, setIsRemovingParticipant] = useState(false);
  const [isAddingGathering, setIsAddingGathering] = useState(false);
  const [copiedReferenceID, setCopiedReferenceID] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (participantID) {
        const participant = await FetchParticipant(participantID, type);
        if (participant) {
          console.log(participant);
          const gatheringIDsArray = Array.isArray(participant.gatheringIDs)
            ? participant.gatheringIDs
            : Object.values(participant.gatheringIDs || {});

          setParticipantData({
            name: participant.name,
            email: participant.email,
            website: participant.website || "",
            description: participant.description || "",
            gatheringIDs: gatheringIDsArray,
            participantReferenceID: participant.referenceID,
            socialLinks: Array.isArray(participant.socialLinks)
              ? participant.socialLinks
              : [],
            imagePath: participant.imagePath || "",
          });

          console.log(gatheringIDsArray);

          if (gatheringIDsArray.length !== 0) {
            const fetchedGatherings =
              await FetchParticipantGatherings(gatheringIDsArray);
            console.log(fetchedGatherings);
            setGatherings(fetchedGatherings);
          }
        }
      }
    };
    loadData();
  }, [participantID, refreshCounter, type]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setParticipantData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const toggleEditMode = () => {
    setIsEditable(!isEditable);
  };

  const handleCopyParticipantReferenceID = async () => {
    if (!participantData.participantReferenceID) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        participantData.participantReferenceID,
      );
      setCopiedReferenceID(true);
      setTimeout(() => setCopiedReferenceID(false), 2500);
    } catch (error) {
      console.error("Failed to copy participant reference ID:", error);
      alert("Could not copy Reference ID. Please copy it manually.");
    }
  };

  const handleSocialLinkChange = (field, value) => {
    setNewSocialLink((prev) => ({ ...prev, [field]: value }));
  };

  const addSocialLink = () => {
    if (!newSocialLink.name || !newSocialLink.url) {
      alert("Please fill in both fields.");
      return;
    }

    const normalizedSocialLink = {
      ...newSocialLink,
      url: normalizeOptionalUrl(newSocialLink.url),
    };

    setParticipantData((prev) => ({
      ...prev,
      socialLinks: [...(prev.socialLinks || []), normalizedSocialLink],
    }));

    setNewSocialLink({ name: "", url: "" });
  };

  const removeSocialLink = (index) => {
    setParticipantData((prev) => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index),
    }));
  };

  const renderSocialLinkOptions = () => {
    if (type === "Artist") {
      return (
        <>
          <option value="">Select Platform</option>
          <option value="Spotify">Spotify</option>
          <option value="Apple Music">Apple Music</option>
          <option value="TIDAL">TIDAL</option>
          <option value="Bandcamp">Bandcamp</option>
          <option value="Soundcloud">Soundcloud</option>
          <option value="Other">Other</option>
        </>
      );
    } else {
      return (
        <>
          <option value="">Select Platform</option>
          <option value="Facebook">Facebook</option>
          <option value="Twitter">Twitter</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Instagram">Instagram</option>
          <option value="Other">Other</option>
        </>
      );
    }
  };

  const saveChanges = async () => {
    try {
      const participantDataToSave = {
        ...participantData,
        type: participantData.type || type,
      };

      if (imageFile) {
        try {
          const entityType = type.toLowerCase();

          await DeleteExistingImage(participantID, entityType);

          const imagePath = await uploadImage(
            imageFile,
            participantID,
            entityType,
          );

          participantDataToSave.imagePath = imagePath;
        } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload the image. Please try again.");
          return;
        }
      }

      const normalizedParticipantData = {
        ...participantDataToSave,
        website: normalizeOptionalUrl(participantDataToSave.website),
        socialLinks: (participantDataToSave.socialLinks || []).map((link) => ({
          ...link,
          url: normalizeOptionalUrl(link.url),
        })),
      };

      const result = PartialParticipantSchema.safeParse(
        normalizedParticipantData,
      );

      if (!result.success) {
        console.error("Validation errors:", result.error.errors);
        const validationErrors = result.error.errors.reduce((acc, err) => {
          acc[err.path[0]] = err.message;
          return acc;
        }, {});
        setErrors(validationErrors);
        alert("Please correct the errors before saving.");
        return;
      }

      const success = await ModifyParticipant(
        participantID,
        normalizedParticipantData,
        type,
      );

      if (success) {
        setParticipantData(normalizedParticipantData);
        alert("Changes saved successfully.");
        setIsEditable(false);
      } else {
        alert("Failed to save changes.");
      }
    } catch (error) {
      console.error("An error occurred while saving changes:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setImageFile(file);
  };

  const openAddGatheringModal = () => {
    setIsAddGatheringModalOpen(true);
  };

  const closeAddGatheringModal = () => {
    setIsAddGatheringModalOpen(false);
    setNewGatheringReferenceID("");
  };

  const handleAddGathering = async () => {
    if (isAddingGathering) {
      return;
    }

    const trimmedReferenceID = newGatheringReferenceID.trim();

    if (!trimmedReferenceID) {
      alert("Please enter a Gathering Reference ID.");
      return;
    }

    const participantReferenceID = participantData.participantReferenceID;

    if (!participantReferenceID) {
      alert(
        "Participant Reference ID is missing. Please refresh the page and try again.",
      );
      return;
    }

    setIsAddingGathering(true);

    try {
      const result = await ModifyParticipantGatherings(
        participantReferenceID,
        trimmedReferenceID,
        type,
        "add",
      );

      if (result.success) {
        alert("Gathering added successfully.");

        setParticipantData((prevData) => {
          const existingGatheringIDs = prevData.gatheringIDs || [];

          if (existingGatheringIDs.includes(result.gatheringID)) {
            return prevData;
          }

          return {
            ...prevData,
            gatheringIDs: [...existingGatheringIDs, result.gatheringID],
          };
        });

        setRefreshCounter((prev) => prev + 1);
        closeAddGatheringModal();
      } else {
        alert("Failed to add the Gathering.");
      }
    } catch (error) {
      console.error("Error adding gathering:", error);
      alert("An unexpected error occurred.");
    } finally {
      setIsAddingGathering(false);
    }
  };

  const handleRemoveParticipant = async (
    participantReferenceID,
    gatheringReferenceID,
  ) => {
    console.log("test");
    try {
      if (!participantReferenceID || !gatheringReferenceID) {
        console.log("no reference id");
        return;
      }

      setIsRemovingParticipant(true);

      const result = await ModifyParticipantGatherings(
        participantReferenceID,
        gatheringReferenceID,
        type,
        "remove",
      );

      if (result.success) {
        alert("Gathering removed successfully.");

        setParticipantData((prevData) => ({
          ...prevData,
          gatheringIDs: prevData.gatheringIDs.filter(
            (docID) => docID !== result.gatheringID,
          ),
        }));

        setGatherings((prevGathers) =>
          prevGathers.filter((g) => g.id !== result.gatheringID),
        );
      } else {
        alert("Failed to remove the Gathering.");
      }
    } catch (error) {
      console.error("Error removing gathering:", error);
      alert("An unexpected error occurred.");
    } finally {
      setIsRemovingParticipant(false);
    }
  };

  const openScheduleModal = async (gatheringID) => {
    try {
      const fetchedEvents = await FetchEvents(gatheringID);
      setEvents(fetchedEvents);
      setSelectedSchedule(gatheringID);
      setIsScheduleModalOpen(true);
    } catch (error) {
      console.error("Error fetching schedule for Gathering:", error);
    }
  };

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setSelectedSchedule(null);
    setEvents([]);
  };

  const openGatheringModal = (gathering) => {
    setSelectedGathering(gathering);
    setIsGatheringModalOpen(true);
  };

  const closeGatheringModal = () => {
    setSelectedGathering(null);
    setIsGatheringModalOpen(false);
  };

  const participantInitial = (participantData.name || type || "P")
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <div className="participant-dashboard-container">
      <button className="return-home-button" onClick={() => navigate("/")}>
        Go back
      </button>

      <div className="dashboard-content">
        <div className="participant-content">
          <div className="rounded-div participant-details">
            <div className="profile-layout">
              <aside className="profile-sidebar">
                <div className="profile-avatar-shell">
                  {participantData.imagePath ? (
                    <img
                      src={participantData.imagePath}
                      alt="Profile"
                      className="profile-image profile-avatar-image"
                    />
                  ) : (
                    <div className="profile-avatar-fallback">{participantInitial}</div>
                  )}
                </div>

                <input
                  id="image-upload"
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="upload-button"
                  disabled={!isEditable}
                />

                <label
                  className={`upload-button-label ${!isEditable ? "is-disabled" : ""}`}
                  htmlFor="image-upload"
                >
                  Upload New Profile Image
                </label>

                {imageFile && (
                  <p className="selected-file-text">
                    <strong>Selected File:</strong> {imageFile.name}
                  </p>
                )}

                <div className="profile-actions">
                  <button className="edit-mode-button" onClick={toggleEditMode}>
                    {isEditable ? "Disable Editing" : "Enable Editing"}
                  </button>

                  {isEditable && (
                    <button className="save-changes-button" onClick={saveChanges}>
                      Save Changes
                    </button>
                  )}
                </div>

                {participantData.participantReferenceID && (
                  <div className="participant-reference-id-box compact">
                    <p className="participant-reference-id-label">
                      {type} Reference ID:
                    </p>

                    <div className="participant-reference-id-row">
                      <span className="participant-reference-id-value">
                        {participantData.participantReferenceID}
                      </span>

                      <button
                        type="button"
                        className="copy-reference-id-button"
                        onClick={handleCopyParticipantReferenceID}
                      >
                        {copiedReferenceID ? "Copied!" : "Copy"}
                      </button>
                    </div>

                    <p className="participant-reference-id-help">
                      Share this ID with event hosts so they can add your profile to
                      their event.
                    </p>
                  </div>
                )}
              </aside>

              <section className="profile-main">
                <div className="profile-heading-block">
                  <p className="profile-eyebrow">{type} Profile</p>
                  <h3 className="profile-title">
                    {participantData.name || `${type} Dashboard`}
                  </h3>
                  <p className="profile-subtitle">
                    Keep your profile, contact details, and event access current.
                  </p>
                </div>

                <div className="profile-info-grid">
                  <div className="profile-info-column">
                    <h4>Your Info</h4>

                    <p className="field-label">Name</p>
                    <input
                      className="input-field"
                      type="text"
                      name="name"
                      placeholder={`${type} Name`}
                      value={participantData.name || ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                    />
                    {errors.name && <p className="error-message">{errors.name}</p>}

                    <p className="field-label">Email</p>
                    <input
                      className="input-field"
                      type="email"
                      name="email"
                      value={participantData.email || ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                    />
                    {errors.email && <p className="error-message">{errors.email}</p>}

                    <p className="field-label">Website</p>
                    <input
                      className="input-field"
                      type="text"
                      name="website"
                      value={participantData.website || ""}
                      onChange={handleInputChange}
                      placeholder="Website, ex: google.com"
                      disabled={!isEditable}
                    />
                    {errors.website && (
                      <p className="error-message">{errors.website}</p>
                    )}

                    <p className="field-label">Description</p>
                    <textarea
                      className="input-field"
                      name="description"
                      value={participantData.description || ""}
                      onChange={handleInputChange}
                      disabled={!isEditable}
                    />
                    {errors.description && (
                      <p className="error-message">{errors.description}</p>
                    )}
                  </div>

                  <div className="profile-info-column profile-social-column">
                    <h4>Social Links</h4>

                    {participantData.socialLinks.map((link, index) => (
                      <div key={index} className="social-link">
                        <span>
                          <strong>{link.name}:</strong> {link.url}
                        </span>
                        {isEditable && (
                          <button onClick={() => removeSocialLink(index)}>
                            Remove
                          </button>
                        )}
                      </div>
                    ))}

                    {isEditable ? (
                      <div className="social-link-inputs">
                        <select
                          className="input-field"
                          value={newSocialLink.name}
                          onChange={(e) =>
                            handleSocialLinkChange("name", e.target.value)
                          }
                        >
                          {renderSocialLinkOptions()}
                        </select>

                        <input
                          className="input-field"
                          type="text"
                          placeholder="URL, ex: spotify.com/artist/example"
                          value={newSocialLink.url}
                          onChange={(e) =>
                            handleSocialLinkChange("url", e.target.value)
                          }
                        />

                        <button onClick={addSocialLink}>Add</button>
                      </div>
                    ) : (
                      <p className="instruction-text">
                        Click "Enable Editing" to add your social links.
                      </p>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="rounded-div participant-gatherings gatherings-card">
            <div className="header">
              <h2>Your Registered Events</h2>
              <button className="add-gathering-button" onClick={openAddGatheringModal}>
                Add a New Event
              </button>
            </div>

            <table className="gatherings-table">
              <thead style={{ backgroundColor: "#E5F8FF" }}>
                <tr>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Location</th>
                  {type === "Artist" && <th>View Schedule</th>}
                  <th>Delete</th>
                </tr>
              </thead>

              <tbody>
                {gatherings.map((gathering) => (
                  <tr key={gathering.id}>
                    <td onClick={() => openGatheringModal(gathering)}>
                      {gathering.gatheringName}
                    </td>
                    <td>
                      {gathering.startDate} - {gathering.endDate}
                    </td>
                    <td>{gathering.location}</td>

                    {type === "Artist" && (
                      <td>
                        <button onClick={() => openScheduleModal(gathering.id)}>
                          View Schedule
                        </button>
                      </td>
                    )}

                    <td>
                      <button
                        onClick={() =>
                          handleRemoveParticipant(
                            participantData.participantReferenceID,
                            gathering.gatheringReferenceID,
                          )
                        }
                      >
                        {isRemovingParticipant ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal isOpen={isAddGatheringModalOpen} onClose={closeAddGatheringModal}>
        <h4>Add Gathering by Reference ID</h4>
        <p>Contact the Event Host for the unique Reference ID.</p>

        <input
          type="text"
          className="input-field"
          value={newGatheringReferenceID}
          onChange={(e) => setNewGatheringReferenceID(e.target.value)}
          placeholder="Enter Gathering Reference ID"
          disabled={isAddingGathering}
        />

        <button onClick={handleAddGathering} disabled={isAddingGathering}>
          {isAddingGathering ? "Adding..." : "Submit"}
        </button>

        {isAddingGathering && (
          <p style={{ marginTop: 8, fontSize: "0.9rem", color: "#4a5568" }}>
            Adding event. Please wait...
          </p>
        )}
      </Modal>

      {isScheduleModalOpen && (
        <div className="custom-modal-overlay" onClick={closeScheduleModal}>
          <div
            className="custom-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close-button" onClick={closeScheduleModal}>
              &times;
            </button>

            <div className="modal-header">
              <h2>Schedule for Gathering</h2>
            </div>

            <div className="modal-body">
              {selectedSchedule && (
                <ScheduleList
                  schedule={events}
                  handleDeleteEvent={() => {}}
                  startDate={
                    gatherings.find((g) => g.id === selectedSchedule)?.startDate
                  }
                  endDate={
                    gatherings.find((g) => g.id === selectedSchedule)?.endDate
                  }
                  isArtistScheduleList={true}
                  artistID={participantID}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={isGatheringModalOpen} onClose={closeGatheringModal}>
        {selectedGathering && (
          <div
            style={{
              minWidth: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              textAlign: "left",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <h2>Gathering Details</h2>
            <p>
              <strong>Name:</strong> {selectedGathering.gatheringName}
            </p>
            <p>
              <strong>Date:</strong> {selectedGathering.startDate} -{" "}
              {selectedGathering.endDate}
            </p>
            <p>
              <strong>Location:</strong> {selectedGathering.location}
            </p>
            <p>
              <strong>Host Name:</strong> {selectedGathering.hostName}
            </p>
            <p>
              <strong>Host Email:</strong> {selectedGathering.hostEmail}
            </p>
            <p>
              <strong>Host Phone:</strong> {selectedGathering.hostPhone}
            </p>
            <p>
              <strong>Description:</strong> {selectedGathering.description}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ParticipantDashboard;
